import { Button, Drawer, ScrollArea } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { flatMap, pick } from 'lodash-es'
import type { Comment } from '@/@types/comment'
import { ContentType } from '@/@types/common'
import CommentForm from '@/components/Comment/CommentForm'
import { DELETE_COMMENT_MUTATION_OPTIONS } from '@/components/Comment/hooks'
import { showCommentFormModal } from '@/components/Comment/utils'
import {
	addCommentReplyToCache,
	generateCommentQueryKey,
	generatePrimaryCommentsQueryKey,
	getPrimaryCommentsQueryOptions,
	removeCommentReplyFromCache,
} from '@/utils/fetchers/comment'
import { useIsMobileTabBarLayout } from '@/utils/hooks/useMediaQuery'
import { removeFromInfiniteData } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import StoryCommentList from './StoryCommentList'
import styles from './StoryCommentsDrawer.module.css'

export interface StoryCommentsDrawerProps {
	canAddComment: boolean
	onClose: () => void
	onCommentAdded?: (comment: Comment) => void
	onCommentDeleted?: (comment: Comment) => void
	onCommentReplyAdded?: (comment: Comment) => void
	opened: boolean
	storyId: string
}

export default function StoryCommentsDrawer({
	canAddComment,
	onClose,
	onCommentAdded,
	onCommentDeleted,
	onCommentReplyAdded,
	opened,
	storyId,
}: StoryCommentsDrawerProps) {
	const isCompactLayout = useIsMobileTabBarLayout()
	const queryParams = {
		targetId: storyId,
		targetType: ContentType.STORY,
	}
	const queryKey = generatePrimaryCommentsQueryKey(queryParams)
	const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery({
		...getPrimaryCommentsQueryOptions(queryParams),
		enabled: opened,
	})
	const { mutateAsync: deleteComment } = useMutation(DELETE_COMMENT_MUTATION_OPTIONS)
	const comments = flatMap(data?.pages ?? [], (page) => page.data)

	const handleCommentSubmit = (comment: Comment) => {
		queryClient.setQueryData(generateCommentQueryKey(pick(comment, ['id', 'targetType'])), comment)
		void queryClient.invalidateQueries({ queryKey })
		onCommentAdded?.(comment)
	}

	const handleCommentDelete = async (comment: Comment) => {
		const deletedComment = await deleteComment(comment)
		// DELETE_COMMENT 的响应体不保证包含完整的评论字段（如 id），
		// 用请求前已知的 comment 更新本地缓存，避免因响应数据缺失导致后续同步逻辑抛错
		if (comment.level <= 1) {
			removeFromInfiniteData<Comment>(queryKey, comment.id)
		} else {
			removeCommentReplyFromCache(comment)
		}
		onCommentDeleted?.(comment)

		return deletedComment
	}
	const handleCommentReply = (comment: Comment) => {
		const modalId = showCommentFormModal({
			allowSyncToPersonalUpdate: false,
			replyToComment: {
				to: comment,
				threadId: comment.level <= 1 ? undefined : comment.threadId,
			},
			onClose: () => modals.close(modalId),
			onSubmit: (reply) => {
				addCommentReplyToCache(comment, reply)
				onCommentReplyAdded?.(reply)
			},
		})
	}

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position={isCompactLayout ? 'bottom' : 'right'}
			size={isCompactLayout ? 'min(72dvh, 640px)' : 420}
			title="留言"
			closeButtonProps={{ 'aria-label': '关闭留言', radius: 'xl' }}
			overlayProps={{ backgroundOpacity: 0 }}
			classNames={{
				content: styles.content,
				header: styles.header,
				body: styles.body,
				title: styles.title,
				close: styles.close,
			}}
		>
			{canAddComment ? (
				<div className={styles.formContainer}>
					<CommentForm
						autoFocus
						targetId={storyId}
						targetType={ContentType.STORY}
						allowSyncToPersonalUpdate={false}
						placeholder="留言只有你和对方可见"
						submitLabel="留言"
						onSubmit={handleCommentSubmit}
					/>
				</div>
			) : (
				<p className={styles.archivedNotice} role="status">
					已归档的日记无法留言哦
				</p>
			)}

			<ScrollArea className={styles.commentsScroller}>
				<StoryCommentList
					data={comments}
					canReply={canAddComment}
					isLoading={isFetching && comments.length === 0}
					onDelete={handleCommentDelete}
					onReply={handleCommentReply}
				/>

				{hasNextPage && (
					<Button
						variant="subtle"
						loading={isFetchingNextPage}
						className={styles.loadMoreButton}
						onClick={() => void fetchNextPage()}
					>
						加载更多
					</Button>
				)}
			</ScrollArea>
		</Drawer>
	)
}
